'use client'

import { CSSProperties, useCallback } from 'react'
import { useRouter } from 'next/navigation'
import { useSession } from 'next-auth/react'

import { CustomerTypes } from 'modules/customer'
import { theme } from 'shared/ui/utils/designTokens'
import toast from 'shared/ui/utils/toast'
import { getApiErrors } from 'shared/utils/errors'

import { getPaymentLinkForSaleOrder } from '../submodules/payment/api'
import { redirectToPaymentLink } from '../submodules/payment/utils'
import { SaleOrder } from '../types'

import ProfileSaleOrdersListItemTablet from './ProfileSaleOrdersListItemTablet'
import ProfileSaleOrdersListItemWeb from './ProfileSaleOrdersListItemWeb'

import styles from './ProfileSaleOrdersTable.module.scss'

const retryPaymentWidthCssValue = '64px'
const numberOfColumns = 7
const tablegap = theme.spacing[1]

const getColumnWidthCssValue = (width: string) => {
  return `calc(${width} - (${retryPaymentWidthCssValue} / ${numberOfColumns}) - (${tablegap} * ${numberOfColumns - 1}))`
}

const columnWidths = [
  getColumnWidthCssValue('18%'),
  getColumnWidthCssValue('18%'),
  getColumnWidthCssValue('18%'),
  getColumnWidthCssValue('18%'),
  getColumnWidthCssValue('18%'),
  retryPaymentWidthCssValue,
  getColumnWidthCssValue('10%')
]

// `width` is rendered as an HTML attribute on <th> (as before); spread, because
// React's th typings do not declare it
const getCellProps = (width: string) => ({
  width,
  style: { '--cell-width': width } as CSSProperties
})

interface ProfileSaleOrdersTableProps {
  user: CustomerTypes
  orders: SaleOrder[]
  disabledDocuments?: boolean
}

const ProfileSaleOrdersTable = ({
  user,
  orders,
  disabledDocuments = false
}: ProfileSaleOrdersTableProps) => {
  const router = useRouter()
  const session = useSession()
  const token = session.data?.user?.token

  const handleRedirect = (path: string) => () => {
    router.push(path)
  }

  const handlePaymentStart = useCallback(
    async (order: SaleOrder) => {
      try {
        const response = await getPaymentLinkForSaleOrder({
          orderId: order.id,
          token
        })
        const paymentLink = response.data.data?.link || null

        if (paymentLink) {
          redirectToPaymentLink(paymentLink)
        }
      } catch (err) {
        const apiErrors = getApiErrors(err)

        toast.error(apiErrors)
      }
    },
    [token]
  )

  return (
    <>
      <div className={styles.webContent}>
        <table className={styles.orderWrapper}>
          <thead>
            <tr className={styles.row}>
              <th
                className={styles.orderTitle}
                {...getCellProps(columnWidths[0])}
                scope='col'
              >
                Nr zamówienia
              </th>
              <th
                className={styles.orderTitle}
                {...getCellProps(columnWidths[1])}
                scope='col'
              >
                Data zamówienia
              </th>
              <th
                className={styles.orderTitle}
                {...getCellProps(columnWidths[2])}
                scope='col'
              >
                Suma
              </th>
              <th
                className={styles.orderTitle}
                {...getCellProps(columnWidths[3])}
                scope='col'
              >
                Status
              </th>
              <th
                className={styles.orderTitle}
                {...getCellProps(columnWidths[4])}
                scope='col'
                aria-label='Status zwrotu'
              />
              <th
                className={styles.orderTitle}
                {...getCellProps(columnWidths[5])}
                scope='col'
                aria-label='Ponów płatność'
              />
              <th
                className={styles.orderIcons}
                scope='col'
                aria-label='Akcje zamówienia'
              />
            </tr>
          </thead>
          <tbody>
            {user &&
              orders?.map((order: SaleOrder) => (
                <ProfileSaleOrdersListItemWeb
                  columnWidths={columnWidths}
                  disabledDocuments={disabledDocuments}
                  token={token}
                  key={order.id}
                  order={order}
                  user={user}
                  handlePaymentStart={handlePaymentStart}
                  handleRedirect={handleRedirect}
                />
              ))}
          </tbody>
        </table>
      </div>
      <div className={styles.tabletContent}>
        {user &&
          orders?.map(order => (
            <ProfileSaleOrdersListItemTablet
              disabledDocuments={disabledDocuments}
              token={token}
              key={order.id}
              order={order}
              user={user}
              handlePaymentStart={handlePaymentStart}
              handleRedirect={handleRedirect}
            />
          ))}
      </div>
    </>
  )
}

export default ProfileSaleOrdersTable
